import { Icon, Button } from '@cinch/ui';
import React, { Component, useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { array, string, any, func, bool } from 'prop-types';
import { Scoped, a, m } from 'kremling';
import { isEqual, isNumber, set } from 'lodash';
import styles from './static-type-ahead.styles.scss';

// Matches `.type-ahead__content { max-height }` in static-type-ahead.styles.scss.
const LIST_MAX_HEIGHT = 200;
// Only orders the list within its nearest stacking context (for the node modal, .node-modal's
// z-index 900 context): nothing inside the modal sets a higher z-index, so it paints over the footer.
const FLOATING_LIST_Z_INDEX = 1000;
// Gap kept between the floating list and the viewport edge when capping its max-height.
const FLOATING_LIST_VIEWPORT_MARGIN = 8;
// First open render, before the field is measured: already fixed (out of the scroll container's
// flow, so it cannot add a scrollbar or reflow the panel) but hidden until it is positioned.
const FLOATING_LIST_UNMEASURED_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  visibility: 'hidden',
  zIndex: FLOATING_LIST_Z_INDEX
};
export const StaticTypeAhead = props => {
  const contentEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenAbove, setIsOpenAbove] = useState(false);
  const [floatingStyle, setFloatingStyle] = useState(null);
  const [search, setSearch] = useState(props.value || '');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [items, setItems] = useState([]);
  const handleDocumentClick = useCallback(e => {
    if (contentEl.current && !contentEl.current.contains(e.target)) {
      close();
    }
  }, []);
  useEffect(() => {
    return () => {
      // On unmount
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // Opt-in (showAllOnOpen): while the input still shows the selected value (the user has not
  // typed since open/select), list every item instead of filtering down to the selected one.
  const showAll = !!props.showAllOnOpen && search === (props.value || '');
  useEffect(() => {
    let items = [...props.items];
    if (search !== '' && !showAll) {
      setItems([]);
      items = items.filter(i => i[`${props.displayProperty}`].toLowerCase().includes(search.toLowerCase()));
      setItems(items);
    } else {
      // search === '' (or showAll): the full list. Without showAllOnOpen this is the old `search === ''` branch.
      setItems(items);
    }
  }, [props.items, search, showAll]);

  // Opt-in (floatingList): while open, pin the list with position: fixed to the field's viewport
  // rect so an ancestor's overflow (e.g. the node modal's scrolling body) cannot clip it. It stays
  // in this component's DOM (no portal) so the contentEl click-outside check still works.
  const positionFloatingList = useCallback(() => {
    if (!contentEl.current) return;
    const rect = contentEl.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const above = spaceBelow < LIST_MAX_HEIGHT && rect.top > spaceBelow;
    const space = above ? rect.top : spaceBelow;
    const next = {
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: FLOATING_LIST_Z_INDEX,
      top: above ? 'auto' : rect.bottom,
      bottom: above ? viewportHeight - rect.top : 'auto',
      maxHeight: Math.max(0, Math.min(LIST_MAX_HEIGHT, space - FLOATING_LIST_VIEWPORT_MARGIN))
    };
    setIsOpenAbove(above);
    // Returning the previous object when nothing moved lets React bail out, so scroll events
    // (including scrolling inside the list itself) do not re-render the component.
    setFloatingStyle(prev => isEqual(prev, next) ? prev : next);
  }, []);
  useLayoutEffect(() => {
    if (!props.floatingList || !isOpen) {
      setFloatingStyle(null);
      return undefined;
    }
    // The list is already position: fixed + hidden (FLOATING_LIST_UNMEASURED_STYLE), so it is out of
    // the scroll container's flow and cannot add a scrollbar or reflow the panel before we measure.
    positionFloatingList();
    // Belt-and-braces: re-measure once after the browser has laid out the revealed list.
    const frame = window.requestAnimationFrame(positionFloatingList);
    window.addEventListener('resize', positionFloatingList);
    // Capture phase so scrolls of any ancestor scroll container are seen, not just the window.
    window.addEventListener('scroll', positionFloatingList, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', positionFloatingList);
      window.removeEventListener('scroll', positionFloatingList, true);
    };
  }, [props.floatingList, isOpen, positionFloatingList]);
  let floatingListStyle;
  if (props.floatingList && isOpen) {
    floatingListStyle = floatingStyle || FLOATING_LIST_UNMEASURED_STYLE;
  }

  // TODO Check if these functions should useRef or anything like that.
  const open = () => {
    setIsOpen(true);
    document.addEventListener('click', handleDocumentClick);
  };

  // TODO Check if these functions should useRef or anything like that.
  const close = () => {
    setIsOpen(false);
    setIsOpenAbove(false);
    document.removeEventListener('click', handleDocumentClick);
  };
  const onSearch = e => {
    if (!isOpen) open();
    setSearch(e.target.value);
    setSelectedIndex(0);
    if (props.onSearchChange) {
      props.onSearchChange(e.target.value);
    }
  };
  const onFocus = e => {
    e.target.setSelectionRange(0, e.target.value.length);
    if (!isOpen) {
      open();
    }
  };
  const toggle = selectedItem => {
    if (isOpen && !selectedItem) {
      close();
    }
    if (selectedItem) {
      clear();
    }
    if (!isOpen) {
      open();
    }
  };
  const clear = () => {
    props.onChange(null);
    setSearch('');
    setSelectedIndex(0);
  };
  const onSelect = item => {
    const {
      displayProperty
    } = props;
    props.onChange(item);
    setSearch(displayProperty ? item[displayProperty] : item);
    setSelectedIndex(0);
    close();
  };
  const onBlur = () => {
    if (isOpen) {
      close();
    }
  };
  return <Scoped css={styles}>
      <div className="type-ahead" ref={contentEl} style={props.style}>
        {!!props.disabled ? <div className="type-ahead__container">
            <input type="text" className="form-control" value={search} placeholder={props.placeholder} disabled={true} />
          </div> : <>
            <div className="type-ahead__container">
              <input style={{
            ...props.inputStyle
          }} type="text" className={!props.value ? 'form-control placeholderWithoutValue' : 'form-control placeholderStyle'} onChange={onSearch} value={search} onFocus={onFocus} placeholder={props.placeholder} autoComplete="none" onBlur={onBlur} />
            </div>
            <Button icon={props.value ? 'fa-regular-times' : 'fa-solid-caret-down'} actionType="flat" onClick={() => toggle(props.value)} tabIndex={-1} />
            <div className={a('type-ahead__content').m('type-ahead__content--open', isOpen).m('type-ahead__content--above', isOpenAbove)} style={floatingListStyle} data-testid={props.floatingList ? 'type-ahead-floating-list' : undefined}>
              <ul className="select-list select-list__controlled">
                {search !== '' && !items.find(i => i[props.keyProperty] === search) && <li role="option">
                    <a>
                      <div className="select-list__content">{search}</div>
                      <Icon className="select-list__selected" size={14} name="fa-regular-check" />
                    </a>
                  </li>}
                {items.map((item, i) => <li key={props.keyProperty ? item[props.keyProperty] : item} role="option">
                    <a onMouseDown={() => onSelect(item)} className={m('active', selectedIndex === i)} onMouseOver={() => setSelectedIndex(i)}>
                      <div className="select-list__content">{props.displayProperty ? item[props.displayProperty] : item}</div>
                      {isEqual(props.value, props.keyProperty ? item[props.keyProperty] : item) && <Icon className="select-list__selected" size={14} name="fa-regular-check" />}
                    </a>
                  </li>)}
              </ul>
            </div>
          </>}
      </div>
    </Scoped>;
};
StaticTypeAhead.propTypes = {
  showAllOnOpen: bool,
  floatingList: bool
};